import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, Skeleton, type BadgeTone } from "@sagot/ui";
import { REGRESSION_RUNS, runView, tally, type RegressionCase, type RegressionRun, type RunView } from "./regression-model";

const R = "engage.workflows.regression";
const TONES: Record<RunView, BadgeTone> = { never: "neutral", running: "brand", passed: "success", failed: "danger", stale: "warning", error: "danger" };

interface RunCardProps {
  /** `undefined`: still loading; `null`: never run. */
  run: RegressionRun | null | undefined;
  cases: readonly RegressionCase[];
}

/** The last run of the cases: where it stands, and for each case whether each of the three runs passed and why not. */
export function RunCard({ run, cases }: RunCardProps) {
  const { t } = useTranslation();
  if (run === undefined) return <Skeleton className="h-16 rounded-card" />;
  const view = runView(run);
  const names = new Map(cases.map((row) => [row.id, row.name]));
  return (
    <section aria-label={t(`${R}.results.title`)} data-testid="regression-run" data-state={view} className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={TONES[view]}>{t(`${R}.run_state.${view}`, { count: REGRESSION_RUNS })}</Badge>
        {run !== null ? <span className="text-xs text-fg-muted">v{run.version}</span> : null}
      </div>
      {run !== null && run.results.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-fg-muted">
              <tr><th scope="col" className="py-1 pr-3 font-semibold">{t(`${R}.results.case`)}</th><th scope="col" className="py-1 font-semibold">{t(`${R}.results.outcome`)}</th></tr>
            </thead>
            <tbody>
              {run.results.map((row) => (
                <tr key={row.case_id} className="border-t border-line align-top">
                  <th scope="row" className="py-2 pr-3 font-medium">{names.get(row.case_id) ?? t(`${R}.results.removed`)}</th>
                  <td className="py-2">
                    <ul className="flex items-center gap-1" aria-label={t(`${R}.results.passed`, { passed: row.runs.filter(Boolean).length, total: row.runs.length })}>
                      {row.runs.map((ok, index) => (
                        <li key={index} className={ok ? "text-success" : "text-danger"}>
                          <Icon name={ok ? "check-circle" : "x-circle"} aria-hidden />
                          <span className="sr-only">{t(ok ? `${R}.results.runPassed` : `${R}.results.runFailed`, { n: index + 1 })}</span>
                        </li>
                      ))}
                      <li className="ml-1 text-xs text-fg-muted">{tally(row.runs)}</li>
                    </ul>
                    {(row.failures ?? []).length > 0 ? (
                      <ul className="mt-1 list-disc pl-5 text-xs text-fg-muted">
                        {(row.failures ?? []).map((code) => <li key={code}>{t(`${R}.results.failure.${code}`, { defaultValue: code })}</li>)}
                      </ul>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
